import React from 'react';
import { ParteEmpreendimento } from '../../../types/empreendimento';

interface TipologiasProps {
  partesEmpreendimento: ParteEmpreendimento[];
  formatarAreaComUnidade: (area: string | number | undefined) => string;
  formatarMoeda: (valor: string | number | undefined) => string;
}

const getDisponibilidadeLabel = (codigo: number | undefined): string => {
  switch (codigo) {
    case 1: return "Disponível";
    case 2: return "Vendido";
    case 3: return "Distratado";
    case 4: return "Bloqueada";
    default: return '-';
  }
};

export const Segmentos: Record<number, string> = {
  1: 'faixa 1-2',
  2: 'faixa 3',
  3: 'faixa 4',
  4: 'Econômico',
  5: 'Médio',
  6: 'Alto',
  7: 'Luxo',
  8: 'Alto Luxo',
  9: 'Popular',
};

const formatarOrigemRecurso = (origem: number | undefined): string => {
  if (!origem) return '-';
  const origens = {
    1: 'Próprio',
    2: 'SFH',
    3: 'Condomínio',
    4: 'Cooperativa',
    5: 'Próprio+SFH',
    6: 'OUTROS',
    7: 'MCMV'
  };
  return origens[origem as keyof typeof origens] || `Origem ${origem}`;
};

const Tipologias: React.FC<TipologiasProps> = ({ partesEmpreendimento, formatarAreaComUnidade, formatarMoeda }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="px-6 py-5 border-b border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900">Unidades</h3>
        <p className="mt-1 text-sm text-gray-500">Unidades e tipos disponíveis</p>
      </div>

      <div className="p-6">
        {partesEmpreendimento.length > 0 ? (
          <div className="space-y-6">
            {partesEmpreendimento.map((parte,index) => {
                const numeroIncremental = index + 1
              const valor_m2 =
                parte.area_privativa_m2 && parte.valor_imovel
                    ? (Number(parte.valor_imovel) / Number(parte.area_privativa_m2)).toFixed(2)
                    : undefined;

              // Concatenar Torre / Andar / Apt ou marcar como unidade avulsa
              // Usa o nome customizado da torre/pavimento se disponível, senão usa número/padrão
              const nomeTorre = parte.torre_texto || (parte.torre ? `Torre ${parte.torre}` : '-');
              const nomePavimento = parte.pavimento_texto || (parte.andar ? `Andar ${parte.andar}` : '-');
              const unidadeDescricao = (parte.torre || parte.andar || parte.numero_apartamento)
                ? `${nomeTorre}, ${nomePavimento}, Apt ${parte.numero_apartamento || '-'}`
                : 'Unidade avulsa';

              // Agrupar atributos extras em texto legível
              const atributosExtrasTexto = parte.atributos_extras
                ? Object.entries(parte.atributos_extras)
                    .filter(([_, value]) => value !== null && value !== undefined)
                    .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`)
                    .join(', ')
                : '-';

              return (
                <div key={parte.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">{parte.nome}</h4>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {numeroIncremental}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Área Privativa</label>
                      <p className="text-sm font-medium text-gray-900">{formatarAreaComUnidade(parte.area_privativa_m2)}</p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Valor do Imóvel</label>
                      <p className="text-sm font-medium text-gray-900">{formatarMoeda(parte.valor_imovel)}</p>
                    </div>

                    {valor_m2 && (
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Valor/m²</label>
                        <p className="text-sm font-medium text-gray-900">{formatarMoeda(valor_m2)}</p>
                      </div>
                    )}

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Forma de Pagamento</label>
                      <p className="text-sm font-medium text-gray-900">{parte.forma_pagamento || '-'}</p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Disponibilidade</label>
                      <p className="text-sm font-medium text-gray-900">{getDisponibilidadeLabel(parte.disponibilidade)}</p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Vagas de Garagem</label>
                      <p className="text-sm font-medium text-gray-900">{parte.quantidade_garagem ?? '-'}</p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Torre / Andar / Apt</label>
                      <p className="text-sm font-medium text-gray-900">{unidadeDescricao}</p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Segmento</label>
                      <p className="text-sm font-medium text-gray-900">{ Segmentos[parte.segmento]}</p>
                    </div>

                {parte.atributos_extras && Object.keys(parte.atributos_extras).length > 0 && (
                <div className="md:col-span-3">
                    <label className="text-sm font-semibold text-gray-900 uppercase tracking-wide block mb-2 mt-3 ">Atributos Extras</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(parte.atributos_extras)
                        .filter(([_, value]) => value !== null && value !== undefined)
                        .map(([key, value]) => (
                        <div key={key} className="space-y-1">
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {key.replace(/_/g, ' ')}
                            </label>
                            <p className="text-sm font-medium text-gray-900">{value}</p>
                        </div>
                        ))}
                    </div>
                </div>
                )}


                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Nenhuma tipologia encontrada.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tipologias;
